<template>
  <div>
    <ts-page-title
      :title="$t('driverPickupStatistic.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        { text: $t('driverPickupStatistic.pageTitle'), active: true },
      ]"
    />

    <ts-panel class="driver-performance-panel">
      <ts-loading-banner :loading="loading">
        <div
          class="tw-grid tw-grid-cols-1 tw-gap-3 tw-bg-gray-50 tw-p-3 md:tw-grid-cols-3"
        >
          <div
            v-for="card in summaryCards"
            :key="card.key"
            class="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-4 tw-shadow-sm"
          >
            <div
              class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500"
            >
              {{ card.label }}
            </div>
            <div class="tw-mt-3 tw-flex tw-items-end tw-gap-1">
              <span
                class="tw-text-2xl tw-font-bold"
                :style="{ color: card.color }"
                >{{ numberFormat(card.value) }}</span
              >
              <span class="tw-text-xs tw-font-semibold tw-text-gray-500">{{
                card.suffix
              }}</span>
            </div>
          </div>
        </div>

        <ts-panel-wrapper class="driver-performance-filter tw-pb-3">
          <div
            class="tw-grid tw-grid-cols-1 tw-items-end tw-gap-3 lg:tw-grid-cols-12"
          >
            <div class="lg:tw-col-span-4">
              <label
                class="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600"
                >{{ $t("driverPickupStatistic.dateRange") }}</label
              >
              <a-range-picker
                v-model="dateRange"
                :allowClear="false"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                style="width: 100%"
              />
            </div>
            <div class="lg:tw-col-span-4">
              <label
                class="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600"
                >{{ $t("driverPickupStatistic.driverName") }}</label
              >
              <a-select
                v-model="driverIds"
                show-search
                allow-clear
                mode="multiple"
                style="width: 100%"
                :filter-option="false"
                :not-found-content="null"
                :loading="driverSearchLoading"
                :placeholder="$t('driverPickupStatistic.typeToSearch')"
                @search="handleDriverSearch"
              >
                <a-select-option
                  v-for="driver in drivers"
                  :key="driver.driver_id"
                  :value="driver.driver_id"
                >
                  {{ driver.driver_code }} | {{ driver.driver_name }}
                </a-select-option>
              </a-select>
            </div>
            <div
              class="tw-flex tw-h-8 tw-items-stretch tw-gap-2 lg:tw-col-span-4"
            >
              <div
                class="tw-flex tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-rounded-md tw-bg-gray-100 tw-p-0.5"
              >
                <button
                  v-for="preset in datePresets"
                  :key="preset.key"
                  type="button"
                  class="tw-flex-1 tw-rounded tw-px-2 tw-text-xs"
                  :class="
                    isRangePreset(preset.key)
                      ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm'
                      : 'tw-text-gray-600'
                  "
                  @click="applyRangePreset(preset.key)"
                >
                  {{ preset.label }}
                </button>
              </div>
              <ts-button color="primary" @click.prevent="fetchData({ page: 1 })"
                ><i class="fas fa-eye tw-mr-1" />{{ $t("preview") }}</ts-button
              >
            </div>
          </div>
        </ts-panel-wrapper>

        <div
          class="tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-b tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-semibold tw-text-gray-700">{{
              $t("driverPickupStatistic.pickupBreakdown")
            }}</span>
            <span class="tw-h-3 tw-w-px tw-bg-gray-300" />
            <span class="tw-text-xs tw-text-gray-400">{{
              $t("driverPickupStatistic.valuesStatusRatio")
            }}</span>
          </div>
          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
            <div
              v-for="status in pickupBars({})"
              :key="status.key"
              class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-600"
            >
              <span
                class="tw-h-2 tw-w-2 tw-rounded-full"
                :style="{ backgroundColor: status.color }"
              />
              {{ status.label }}
            </div>
          </div>
        </div>
        <div class="tw-pb-4">
          <div v-if="resources.length">
            <article
              v-for="(record, index) in resources"
              :key="record.driver_id"
              class="tw-flex tw-flex-col tw-gap-2 tw-border-b tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 dark:tw-bg-gray-800 lg:tw-flex-row lg:tw-items-center lg:tw-gap-3"
            >
              <div
                class="tw-flex tw-items-center tw-gap-2 lg:tw-w-48 lg:tw-flex-shrink-0"
              >
                <div
                  class="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-blue-600 tw-text-xs tw-font-bold tw-text-white"
                >
                  {{ recordNumber(index) }}
                </div>
                <div class="tw-min-w-0 tw-flex-1">
                  <h3
                    class="tw-mb-0 tw-truncate tw-text-xs tw-font-semibold tw-leading-tight tw-text-gray-800 dark:tw-text-white"
                    :title="record.driver_name"
                  >
                    {{ record.driver_name }}
                  </h3>
                  <div
                    class="tw-truncate tw-text-xs tw-text-gray-500 dark:tw-text-gray-400"
                  >
                    {{ record.driver_code }}
                  </div>
                </div>
              </div>

              <div
                class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-border tw-border-blue-100 tw-bg-blue-50 tw-px-3 tw-py-1.5 dark:tw-border-blue-800 dark:tw-bg-gray-700 lg:tw-w-24 lg:tw-flex-shrink-0"
              >
                <div
                  class="tw-whitespace-nowrap tw-text-xs tw-font-bold tw-uppercase tw-leading-none tw-text-blue-500 dark:tw-text-blue-300"
                >
                  {{ $t("driverPickupStatistic.pickups") }}
                </div>
                <div
                  class="tw-text-xs tw-font-bold tw-leading-none tw-text-blue-600 dark:tw-text-blue-400"
                >
                  {{ numberFormat(record.assigned_pickups) }}
                </div>
              </div>

              <div
                class="tw-grid tw-flex-1 tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3"
              >
                <div
                  v-for="status in pickupBars(record)"
                  :key="status.key"
                  class="tw-rounded-md tw-px-2 tw-py-1"
                  :class="{ 'tw-opacity-40': status.value === 0 }"
                  :style="{ backgroundColor: '#ffffff' }"
                >
                  <div
                    class="tw-flex tw-items-center tw-justify-between tw-gap-1"
                  >
                    <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                      <span
                        class="tw-h-1.5 tw-w-1.5 tw-flex-shrink-0 tw-rounded-full"
                        :style="{ backgroundColor: status.color }"
                      />
                      <span
                        class="tw-truncate tw-text-xs tw-font-semibold tw-text-gray-600"
                        >{{ status.label }}</span
                      >
                    </div>
                    <span
                      class="tw-flex-shrink-0 tw-text-xs tw-font-semibold"
                      :style="{
                        color: status.value > 0 ? status.textColor : '#94a3b8',
                      }"
                      >{{ pickupPercentage(record, status.value) }}%</span
                    >
                  </div>
                  <div
                    class="tw-my-1 tw-text-xs tw-font-bold tw-leading-none tw-text-gray-800"
                  >
                    {{ numberFormat(status.value) }}
                  </div>
                  <div
                    class="tw-h-1 tw-overflow-hidden tw-rounded-full"
                    :style="{
                      backgroundColor:
                        status.value > 0 ? status.trackColor : '#e2e8f0',
                    }"
                  >
                    <div
                      class="tw-h-full tw-rounded-full tw-transition-all tw-duration-500"
                      :style="{
                        width: pickupBarWidth(record, status.value),
                        backgroundColor: status.color,
                      }"
                    />
                  </div>
                </div>
              </div>
            </article>
          </div>
          <div
            v-else-if="!loading"
            class="tw-mx-4 tw-mt-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-px-6 tw-py-8"
            style="min-height: 240px"
          >
            <a-empty :description="$t('driverPickupStatistic.noRecords')" />
            <p
              class="tw-mb-0 tw-mt-2 tw-max-w-md tw-text-center tw-text-sm tw-text-gray-500"
            >
              {{ $t("driverPickupStatistic.emptyHint") }}
            </p>
          </div>
          <div
            v-if="resources.length"
            class="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
          >
            <div class="tw-text-xs tw-text-gray-500">
              {{ paginationSummary }}
            </div>
            <ts-pagination v-model="pagination" @navigate="fetchData" />
          </div>
        </div>
      </ts-loading-banner>
    </ts-panel>
  </div>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
  name: "driverPickupStatistic",
  data() {
    return { loading: false, driverSearchLoading: false, drivers: [] };
  },
  computed: {
    ...mapState("report/driverPickupStatistic", [
      "resources",
      "pagination",
      "summary",
    ]),
    dateRange: {
      get() {
        return this.$store.state.report.driverPickupStatistic.dateRange;
      },
      set(value) {
        this.$store.commit(
          "report/driverPickupStatistic/SET_DATE_RANGE",
          value
        );
      },
    },
    driverIds: {
      get() {
        return this.$store.state.report.driverPickupStatistic.driverIds;
      },
      set(value) {
        this.$store.commit(
          "report/driverPickupStatistic/SET_DRIVER_IDS",
          value || []
        );
      },
    },
    summaryCards() {
      return [
        {
          key: "assigned",
          label: this.$t("driverPickupStatistic.assignedPickups"),
          value: this.summary.assigned_pickups,
          suffix: "requests",
          color: "#2563eb",
        },
        {
          key: "picked",
          label: this.$t("driverPickupStatistic.pickedUp"),
          value: this.summary.picked_up,
          suffix: "requests",
          color: "#059669",
        },
        {
          key: "delivery",
          label: this.$t("driverPickupStatistic.packagesOnDelivery"),
          value: this.summary.packages_on_delivery,
          suffix: "packages",
          color: "#7c3aed",
        },
      ];
    },
    datePresets() {
      return [
        { key: "today", label: this.$t("driverPickupStatistic.today") },
        { key: "last7Days", label: this.$t("driverPickupStatistic.last7Days") },
        { key: "thisMonth", label: this.$t("driverPickupStatistic.thisMonth") },
      ];
    },
    paginationSummary() {
      const total = Number(this.pagination.total) || 0;

      if (!total) return "Showing 0 entries";

      const from = Number(this.pagination.from) || 1;
      const to = Math.min(Number(this.pagination.to) || from, total);

      return "Showing " + from + " to " + to + " of " + total + " entries";
    },
  },
  created() {
    this.handleDriverSearch();
    this.fetchData();
  },
  methods: {
    ...mapActions("report/driverPickupStatistic", ["fetch"]),
    ...mapActions("report/formViewModel", ["fetchFormView"]),
    numberFormat(value) {
      return new Intl.NumberFormat().format(Number(value) || 0);
    },
    recordNumber(index) {
      return (
        (this.pagination.currentPage - 1) * this.pagination.perPage + index + 1
      );
    },
    pickupBars(record) {
      const assigned = Number(record.assigned_pickups) || 0;
      const pickedUp = Number(record.picked_up) || 0;

      return [
        {
          key: "not_picked_up",
          label: this.$t("driverPickupStatistic.notPickedUp"),
          value: Math.max(assigned - pickedUp, 0),
          color: "#f59e0b",
          textColor: "#b45309",
          trackColor: "#fef3c7",
        },
        {
          key: "picked_up",
          label: this.$t("driverPickupStatistic.pickedUp"),
          value: pickedUp,
          color: "#10b981",
          textColor: "#047857",
          trackColor: "#d1fae5",
        },
        {
          key: "packages_on_delivery",
          label: this.$t("driverPickupStatistic.onDelivery"),
          value: Number(record.packages_on_delivery) || 0,
          color: "#3b82f6",
          textColor: "#1d4ed8",
          trackColor: "#dbeafe",
        },
      ];
    },
    pickupPercentage(record, value) {
      const total = Number(record.assigned_pickups) || 0;

      if (!total) return 0;

      return Number((((Number(value) || 0) / total) * 100).toFixed(1));
    },
    pickupBarWidth(record, value) {
      const percentage = this.pickupPercentage(record, value);

      if (!percentage) return "0%";

      return Math.min(Math.max(percentage, 3), 100) + "%";
    },
    presetRange(preset) {
      const end = moment();
      const start =
        preset === "last7Days"
          ? end.clone().subtract(6, "days")
          : preset === "thisMonth"
          ? end.clone().startOf("month")
          : end.clone();
      return [start.format("DD-MM-YYYY"), end.format("DD-MM-YYYY")];
    },
    isRangePreset(preset) {
      const range = this.presetRange(preset);
      return this.dateRange[0] === range[0] && this.dateRange[1] === range[1];
    },
    applyRangePreset(preset) {
      this.dateRange = this.presetRange(preset);
    },
    fetchData(attributes = {}) {
      this.loading = true;
      this.fetch(attributes)
        .catch((error) => this.$notify({ type: "error", text: error.message }))
        .finally(() => {
          this.loading = false;
        });
    },
    handleDriverSearch: debounce(function (value) {
      this.driverSearchLoading = true;
      this.fetchFormView({ fnName: "driver", search_driver: value })
        .then((response) => {
          this.drivers = response.data.driver;
        })
        .catch((error) => this.$notify({ type: "error", text: error.message }))
        .finally(() => {
          this.driverSearchLoading = false;
        });
    }, 500),
  },
};
</script>

<style scoped lang="scss">
.driver-performance-panel {
  border: 1px solid #e5edf7;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.driver-performance-panel >>> .card,
.driver-performance-panel >>> .card-body {
  border: 0;
  border-radius: 12px;
}

.driver-performance-panel >>> .card-body {
  padding: 0;
}

.driver-performance-filter {
  background: #fff;
  padding: 14px 16px 16px;
}

.driver-performance-panel >>> .ant-calendar-picker-input,
.driver-performance-panel >>> .ant-select-selection {
  min-height: 34px;
  border-color: #d8e2ef;
  border-radius: 6px;
  color: #334155;
  font-size: 12px;
}

.driver-performance-panel >>> .ant-select-selection__placeholder,
.driver-performance-panel >>> .ant-select-search__field__placeholder {
  color: #94a3b8;
}

.driver-performance-panel >>> .pagination {
  margin-bottom: 0;
}

.driver-performance-panel >>> .pagination > .pagination {
  display: flex;
  align-items: center;
  gap: 6px;
}

.driver-performance-panel >>> .pagination .form-control {
  height: 28px;
  min-width: 48px;
  border-color: #d8e2ef;
  border-radius: 6px;
  color: #475569;
  font-size: 12px;
  padding: 2px 8px;
}

.driver-performance-panel >>> .pagination span {
  color: #475569;
  font-size: 12px;
}

.driver-performance-panel >>> .page-link {
  min-width: 28px;
  height: 28px;
  border-color: #e5edf7;
  border-radius: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 14px;
  padding: 6px 8px;
  text-align: center;
}

.driver-performance-panel >>> .page-item.active .page-link {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.driver-performance-panel >>> .page-item.previous .page-link,
.driver-performance-panel >>> .page-item.next .page-link {
  width: 28px;
  overflow: hidden;
  position: relative;
  text-indent: 999px;
}

.driver-performance-panel >>> .page-item.previous .page-link::after {
  content: "‹";
}

.driver-performance-panel >>> .page-item.next .page-link::after {
  content: "›";
}

.driver-performance-panel >>> .page-item.previous .page-link::after,
.driver-performance-panel >>> .page-item.next .page-link::after {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-indent: 0;
}
</style>
